import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Check, Pencil, StickyNote, ShoppingCart, ListTodo } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  koeb: { label: "Skal købes", cls: "bg-amber-500/15 text-amber-600", Icon: ShoppingCart },
  goer: { label: "Skal gøres", cls: "bg-sky-500/15 text-sky-600", Icon: ListTodo },
  note: { label: "Note", cls: "bg-slate-500/20 text-slate-500", Icon: StickyNote },
};

export function NotesBoard() {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [type, setType] = useState("koeb");
  const [filter, setFilter] = useState("all");
  const [hideDone, setHideDone] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    try { const r = await api.get("/notes"); setNotes(r.data); }
    catch { toast.error("Kunne ikke hente noter"); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!text.trim()) return;
    try { await api.post("/notes", { text, type, done: false }); setText(""); load(); }
    catch { toast.error("Kunne ikke tilføje note"); }
  };
  const patch = async (n, changes) => {
    try {
      await api.put(`/notes/${n.id}`, { text: n.text, type: n.type, done: n.done, ...changes });
      load();
    } catch { toast.error("Kunne ikke opdatere note"); }
  };
  const saveEdit = async (n) => {
    if (!editText.trim()) { setEditId(null); return; }
    await patch(n, { text: editText });
    setEditId(null);
  };
  const remove = async (n) => {
    try { await api.delete(`/notes/${n.id}`); load(); }
    catch { toast.error("Kunne ikke slette note"); }
  };

  const filtered = notes.filter((n) => (filter === "all" || n.type === filter) && (!hideDone || !n.done));
  const openCount = notes.filter((n) => !n.done).length;

  return (
    <div className="max-w-3xl space-y-5" data-testid="notes-board">
      <div>
        <h2 className="font-heading text-2xl font-light text-slate-900">Notesblok</h2>
        <p className="text-sm text-slate-500">Skriv ting ned der skal købes, gøres eller huskes. {openCount} åbne.</p>
      </div>

      {/* Add */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 flex flex-col sm:flex-row gap-3">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Skriv en note… fx 'Bestil kaffekapsler'"
          className="flex-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="note-input" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full sm:w-[150px] bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="note-type-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
            <SelectItem value="koeb">Skal købes</SelectItem>
            <SelectItem value="goer">Skal gøres</SelectItem>
            <SelectItem value="note">Note</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={add} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2" data-testid="note-add-btn">
          <Plus className="w-4 h-4" /> Tilføj
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {[["all", "Alle"], ["koeb", "Skal købes"], ["goer", "Skal gøres"], ["note", "Noter"]].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`note-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === id ? "bg-[#38BDF8] text-[#0b1120]" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
        <button onClick={() => setHideDone((v) => !v)} data-testid="note-hide-done"
          className={`ml-auto px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${hideDone ? "bg-emerald-500/20 text-emerald-600" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>
          Skjul færdige
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.map((n) => {
          const T = TYPES[n.type] || TYPES.note;
          return (
            <div key={n.id} className="rounded-xl bg-[#ffffff] border border-[#e2e8f0] px-3 sm:px-4 py-3 flex items-center gap-3" data-testid={`note-${n.id}`}>
              <button onClick={() => patch(n, { done: !n.done })} title="Markér som færdig" data-testid={`note-toggle-${n.id}`}
                className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-colors ${n.done ? "bg-emerald-500 text-[#0b1120]" : "bg-[#f8fafc] border border-[#e2e8f0] text-transparent hover:text-slate-500"}`}>
                <Check className="w-4 h-4" />
              </button>
              {editId === n.id ? (
                <Input value={editText} onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveEdit(n)} onBlur={() => saveEdit(n)} autoFocus
                  className="flex-1 h-8 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid={`note-edit-input-${n.id}`} />
              ) : (
                <span className={`flex-1 text-sm break-words ${n.done ? "line-through text-slate-500" : "text-slate-700"}`}
                  onDoubleClick={() => { setEditId(n.id); setEditText(n.text); }}>{n.text}</span>
              )}
              <Select value={n.type} onValueChange={(t) => patch(n, { type: t })}>
                <SelectTrigger className={`h-7 w-auto gap-1 border-0 px-2.5 text-xs rounded-full shrink-0 ${T.cls}`} data-testid={`note-type-${n.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                  <SelectItem value="koeb">Skal købes</SelectItem>
                  <SelectItem value="goer">Skal gøres</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
              <Button size="icon" variant="ghost" onClick={() => { setEditId(n.id); setEditText(n.text); }}
                className="h-8 w-8 text-slate-500 hover:text-sky-600 shrink-0" data-testid={`note-edit-${n.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(n)}
                className="h-8 w-8 text-slate-500 hover:text-red-600 shrink-0" data-testid={`note-delete-${n.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="rounded-xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-10 text-center text-slate-500" data-testid="notes-empty">
            Ingen noter endnu. Skriv din første ovenfor.
          </div>
        )}
      </div>
    </div>
  );
}
